<template>
  <div class="pdf-signature-status">
    <v-alert
      :type="statusType"
      :color="statusColor"
      prominent
      dense
      class="signature-status-banner mb-0"
      elevation="2"
    >
      <v-row align="center" no-gutters>
        <v-col class="grow">
          <div class="d-flex align-center">
            <v-icon left>{{ statusIcon }}</v-icon>
            <div>
              <div class="font-weight-bold">{{ statusMessage }}</div>
              <div v-if="hasSignatures" class="text-caption mt-1">
                {{ signatureInfo.count }} digital signature{{ signatureInfo.count > 1 ? 's' : '' }} found
              </div>
            </div>
          </div>
        </v-col>
        <v-col class="shrink" v-if="detailsAvailable">
          <v-btn
            small
            outlined
            @click="toggleDetails"
          >
            <v-icon small left>{{ showDetails ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            {{ showDetails ? 'Hide Details' : 'Show Details' }}
          </v-btn>
        </v-col>
      </v-row>
    </v-alert>
    
    <!-- Details Panel -->
    <v-expand-transition>
      <v-card v-if="showDetails && detailsAvailable" flat class="signature-details-panel">
        <v-card-text>
          <h4 class="mb-3">Signature Details</h4>
          <v-list dense>
            <v-list-item
              v-for="(sig, index) in signatureInfo.signatures"
              :key="index"
              class="signature-detail-item"
            >
              <v-list-item-icon>
                <v-icon
                  :color="getSignatureStatusColor(sig)"
                >
                  {{ getSignatureStatusIcon(sig) }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>{{ sig.fieldName }}</strong>
                </v-list-item-title>
                <v-list-item-subtitle v-if="!sig.error">
                  <div>Signer: {{ sig.signerName }}</div>
                  <div v-if="sig.signDate">Date: {{ formatDate(sig.signDate) }}</div>
                  <div class="mt-1">
                    <v-chip
                      x-small
                      :color="sig.isValid ? 'success' : 'error'"
                      text-color="white"
                      class="mr-1"
                    >
                      {{ sig.isValid ? 'Valid' : 'Invalid' }}
                    </v-chip>
                    <v-chip
                      x-small
                      :color="sig.isTrusted ? 'success' : 'warning'"
                      text-color="white"
                      class="mr-1"
                    >
                      {{ sig.isTrusted ? 'Trusted' : 'Untrusted' }}
                    </v-chip>
                    <v-chip
                      v-if="sig.hasChanged"
                      x-small
                      color="error"
                      text-color="white"
                    >
                      Modified
                    </v-chip>
                  </div>
                </v-list-item-subtitle>
                <v-list-item-subtitle v-else class="error--text">
                  {{ sig.message || 'Error validating signature' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          
          <v-alert
            type="info"
            text
            dense
            class="mt-3 mb-0"
          >
            <div class="text-caption">
              <strong>About Digital Signatures:</strong>
              <ul class="mt-1">
                <li><strong>Valid:</strong> Signature is cryptographically valid and the document hasn't been modified</li>
                <li><strong>Trusted:</strong> The certificate used to sign is trusted by your system</li>
                <li><strong>Modified:</strong> The document has been changed since it was signed</li>
              </ul>
            </div>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-expand-transition>
  </div>
</template>

<script>
import pdfSignatureStatusController from '@/controllers/pdfSignatureStatusController';

export default {
  ...pdfSignatureStatusController,
  methods: {
    ...pdfSignatureStatusController.methods,
    getSignatureStatusColor(sig) {
      if (sig.error) return 'error';
      if (sig.isValid && sig.isTrusted && !sig.hasChanged) return 'success';
      if (!sig.isValid || sig.hasChanged) return 'error';
      if (!sig.isTrusted) return 'warning';
      return 'grey';
    },
    getSignatureStatusIcon(sig) {
      if (sig.error) return 'mdi-close-circle';
      if (sig.isValid && sig.isTrusted && !sig.hasChanged) return 'mdi-check-circle';
      if (!sig.isValid || sig.hasChanged) return 'mdi-alert-circle';
      if (!sig.isTrusted) return 'mdi-alert';
      return 'mdi-help-circle';
    },
    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      try {
        const date = new Date(dateString);
        return date.toLocaleString();
      } catch (error) {
        return dateString;
      }
    }
  }
};
</script>

<style scoped>
.pdf-signature-status {
  position: relative;
  z-index: 2;
}

.signature-status-banner {
  border-radius: 0;
  margin-bottom: 0 !important;
}

.signature-details-panel {
  border-top: 2px solid #e0e0e0;
  background-color: #fafafa;
}

.signature-detail-item {
  border-bottom: 1px solid #e0e0e0;
}

.signature-detail-item:last-child {
  border-bottom: none;
}
</style>

